/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import {
  onGetImages,
  onVenueSingleImageUpload,
  onVenueSingleImageUploadReset,
} from "../../../../Store/Slices/venueImageSlice";

export default function useImageUpload({ dispatch, imageData, businessData }) {
  const [coverFile, setCoverFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([null, null, null, null]);
  const [coverImageId, setCoverImageId] = useState(null);
  const [galleryImageIds, setGalleryImageIds] = useState([
    null,
    null,
    null,
    null,
  ]);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  const pendingReplaceCoverId = useRef(null);
  const pendingReplaceGalleryIds = useRef([]);
  const pendingMultiUpload = useRef(null); // { startIndex, files, uploadedCount } for multi-select queue
  const pendingDeleteSlot = useRef(null); // { type: 'cover'|'gallery', galleryIndex?: number } when deleting from DB

  const coverInputRef = useRef(null);
  const galleryInputRefs = useRef([]);

  const savedImages = imageData?.getImagesData ?? [];

  // Prefill saved images
  useEffect(() => {
    if (savedImages.length === 0) return;

    const covers = savedImages
      .filter((i) => (i.image_type || i.type) === "cover")
      .sort((a, b) => (b.id ?? 0) - (a.id ?? 0));

    const gallery = savedImages
      .filter((i) => (i.image_type || i.type) === "gallery")
      .sort(
        (a, b) =>
          (a.display_order ?? 0) - (b.display_order ?? 0) ||
          (a.id ?? 0) - (b.id ?? 0),
      );

    setCoverImageId((prev) => prev ?? covers[0]?.id ?? null);

    const minSlots = 4;
    const galleryIds = gallery.map((g) => g.id);
    while (galleryIds.length < minSlots) galleryIds.push(null);
    setGalleryImageIds(galleryIds);
    setGalleryFiles(Array(galleryIds.length).fill(null));
    pendingReplaceGalleryIds.current = galleryIds.map(() => null);
  }, [savedImages.length]);

  // Handle single image upload success – only clear slot and reset when API has finished (id or error)
  useEffect(() => {
    const id = imageData?.singleImageId;
    const type = imageData?.singleImageType;
    const galleryIndex = imageData?.singleImageGalleryIndex;
    const isLoading = imageData?.singleImageLoading;

    // Don't run completion logic when we're still loading (effect also runs when upload starts)
    if (type == null || isLoading) return;

    if (type === "cover") {
      if (id != null) {
        setCoverImageId(id);
        setCoverFile(null);
        pendingReplaceCoverId.current = null;
        setUploadingSlot(null);
        if (businessData?.id)
          dispatch(onGetImages({ businessId: businessData.id }));
        dispatch(onVenueSingleImageUploadReset());
      } else {
        setCoverImageId(null);
        setCoverFile(null);
        setUploadingSlot(null);
        if (pendingDeleteSlot.current?.type === "cover") {
          pendingDeleteSlot.current = null;
          if (businessData?.id)
            dispatch(onGetImages({ businessId: businessData.id }));
        }
        dispatch(onVenueSingleImageUploadReset());
      }
    } else if (type === "gallery" && galleryIndex != null) {
      setGalleryImageIds((prev) => {
        const next = [...prev];
        while (next.length <= galleryIndex) next.push(null);
        next[galleryIndex] = id ?? null;
        return next;
      });

      if (id != null) {
        setGalleryFiles((prev) => {
          const n = [...prev];
          while (n.length <= galleryIndex) n.push(null);
          n[galleryIndex] = null;
          return n;
        });

        const pending = [...(pendingReplaceGalleryIds.current || [])];
        while (pending.length <= galleryIndex) pending.push(null);
        pending[galleryIndex] = null;
        pendingReplaceGalleryIds.current = pending;
      }

      setUploadingSlot(null);
      if (id != null && businessData?.id)
        dispatch(onGetImages({ businessId: businessData.id }));
      if (pendingDeleteSlot.current?.type === "gallery" && pendingDeleteSlot.current?.galleryIndex === galleryIndex) {
        pendingDeleteSlot.current = null;
        if (businessData?.id)
          dispatch(onGetImages({ businessId: businessData.id }));
        dispatch(onVenueSingleImageUploadReset());
        return;
      }
      dispatch(onVenueSingleImageUploadReset());

      // If this was part of a multi-select, upload the next file in queue (keep loader on all indices until done)
      const multi = pendingMultiUpload.current;
      if (multi && multi.files && multi.indices && (multi.uploadedCount + 1) < multi.files.length) {
        multi.uploadedCount += 1;
        const nextIndex = multi.indices[multi.uploadedCount];
        const nextFile = multi.files[multi.uploadedCount];
        setUploadingSlot({ type: "gallery", indices: multi.indices });
        dispatch(
          onVenueSingleImageUpload({
            businessId: businessData?.id,
            file: nextFile,
            type: "gallery",
            galleryIndex: nextIndex,
            imageId: pendingReplaceGalleryIds.current[nextIndex] ?? undefined,
          }),
        );
      } else if (multi) {
        pendingMultiUpload.current = null;
      }
    }
  }, [
    imageData?.singleImageId,
    imageData?.singleImageType,
    imageData?.singleImageGalleryIndex,
    imageData?.singleImageLoading,
    dispatch,
    businessData?.id,
  ]);

  // Handle error (upload or delete)
  useEffect(() => {
    if (imageData?.singleImageError) {
      setUploadingSlot(null);
      pendingMultiUpload.current = null;
      pendingDeleteSlot.current = null;
      toast.error("Image upload failed");
      dispatch(onVenueSingleImageUploadReset());
    }
  }, [imageData?.singleImageError, dispatch]);

  const handleConfirmDeleteImage = (slot, imageId) => {
    if (imageId == null) return;
    const type = slot === "cover" ? "cover" : "gallery";
    const galleryIndex = slot === "cover" ? null : slot;
    pendingDeleteSlot.current = { type, galleryIndex };
    setUploadingSlot(type === "cover" ? { type: "cover" } : { type: "gallery", index: slot });
    dispatch(
      onVenueSingleImageUpload({
        businessId: businessData?.id,
        imageId,
        type,
        galleryIndex,
        isDeleted: true,
      }),
    );
  };

  const handleRemoveStep4Image = (slot) => {
    // COVER
    if (slot === "cover") {
      if (coverImageId != null) {
        pendingReplaceCoverId.current = coverImageId;
      }
      setCoverImageId(null);
      setCoverFile(null);
      setUploadingSlot(null);
      if (coverInputRef.current) coverInputRef.current.value = "";
      return;
    }

    // GALLERY – slot is index (0, 1, 2, 3, 4, …)
    if (typeof slot === "number" && slot >= 0) {
      const isExtraSlot = slot >= 4;
      if (isExtraSlot && galleryImageIds.length > 4) {
        // Remove extra slot (splice)
        setGalleryImageIds((prev) => {
          const next = [...prev];
          next.splice(slot, 1);
          return next;
        });
        setGalleryFiles((prev) => {
          const next = [...prev];
          next.splice(slot, 1);
          return next;
        });
        const pending = [...(pendingReplaceGalleryIds.current || [])];
        pending.splice(slot, 1);
        pendingReplaceGalleryIds.current = pending;
        galleryInputRefs.current = [];
      } else {
        // Required slot (0–3) or only 4 slots: clear slot but keep position
        if (galleryImageIds[slot] != null) {
          const ids = [...(pendingReplaceGalleryIds.current || [])];
          while (ids.length <= slot) ids.push(null);
          ids[slot] = galleryImageIds[slot];
          pendingReplaceGalleryIds.current = ids;
        }
        setGalleryImageIds((prev) => {
          const next = [...prev];
          next[slot] = null;
          return next;
        });
        setGalleryFiles((prev) => {
          const next = [...prev];
          next[slot] = null;
          return next;
        });
        if (galleryInputRefs.current[slot]) {
          galleryInputRefs.current[slot].value = "";
        }
      }
      setUploadingSlot(null);
    }
  };

  const addGallerySlot = () => {
    setGalleryImageIds((prev) => [...prev, null]);
    setGalleryFiles((prev) => [...prev, null]);
    pendingReplaceGalleryIds.current = [...(pendingReplaceGalleryIds.current || []), null];
  };

  const addGallerySlotsWithFiles = (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    const minSlots = 4;

    // Fill required empty slots first (use current state for empty = no file and no saved id)
    const idsLen = Math.max(minSlots, galleryImageIds?.length ?? minSlots, galleryFiles?.length ?? minSlots);
    const emptyIndices = [];
    for (let i = 0; i < idsLen; i++) {
      if (!galleryFiles[i] && !galleryImageIds[i]) emptyIndices.push(i);
    }
    const fillCount = Math.min(fileList.length, emptyIndices.length);
    const slotsToFill = emptyIndices.slice(0, fillCount);
    const remainingFiles = fileList.slice(fillCount);
    const remainingCount = remainingFiles.length;
    const allIndices = [...slotsToFill];
    for (let r = 0; r < remainingCount; r++) {
      allIndices.push(idsLen + r);
    }

    setGalleryFiles((prev) => {
      const next = [...prev];
      while (next.length < minSlots) next.push(null);
      for (let j = 0; j < fillCount; j++) {
        next[slotsToFill[j]] = fileList[j];
      }
      if (remainingCount > 0) next.push(...remainingFiles);
      return next;
    });

    setGalleryImageIds((prev) => {
      const next = [...prev];
      while (next.length < minSlots) next.push(null);
      for (let k = 0; k < remainingCount; k++) next.push(null);
      return next;
    });

    const pending = [...(pendingReplaceGalleryIds.current || [])];
    while (pending.length <= (allIndices[allIndices.length - 1] ?? 0)) pending.push(null);
    pendingReplaceGalleryIds.current = pending;

    pendingMultiUpload.current = {
      files: fileList,
      indices: allIndices,
      uploadedCount: 0,
    };
    setUploadingSlot({ type: "gallery", indices: allIndices });
    const firstGalleryIndex = allIndices[0];
    dispatch(
      onVenueSingleImageUpload({
        businessId: businessData?.id,
        file: fileList[0],
        type: "gallery",
        galleryIndex: firstGalleryIndex,
        imageId: pendingReplaceGalleryIds.current[firstGalleryIndex] ?? undefined,
      }),
    );
  };

  const validateStep4Images = (setErrors) => {
    const newErrors = {};
    const hasCover = coverFile || coverImageId;
    const galleryLen = Math.max(galleryFiles?.length ?? 4, galleryImageIds?.length ?? 4);
    const galleryTotal = Array.from({ length: galleryLen }, (_, i) => i).filter(
      (i) => galleryFiles[i] || galleryImageIds[i],
    ).length;

    if (!hasCover) newErrors.cover = "Cover image is required";
    if (galleryTotal < 4)
      newErrors.gallery = `At least 4 gallery images required (${galleryTotal}/4)`;

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const getFirstPendingStep4Upload = () => {
    if (coverFile && !coverImageId)
      return { type: "cover", file: coverFile, galleryIndex: null };
    const len = Math.max(4, galleryFiles?.length || 4);
    for (let i = 0; i < len; i++) {
      if (galleryFiles[i] && !galleryImageIds[i])
        return { type: "gallery", file: galleryFiles[i], galleryIndex: i };
    }
    return null;
  };

  const isStep4AllUploaded = () => {
    const coverDone = !!(coverFile || coverImageId);
    const galleryLen = Math.max(galleryFiles?.length ?? 4, galleryImageIds?.length ?? 4);
    const galleryCount = Array.from({ length: galleryLen }, (_, i) => i).filter(
      (i) => galleryFiles[i] || galleryImageIds[i],
    ).length;
    return coverDone && galleryCount >= 4;
  };

  return {
    coverFile,
    setCoverFile,
    galleryFiles,
    setGalleryFiles,
    coverImageId,
    galleryImageIds,
    uploadingSlot,
    setUploadingSlot,
    handleRemoveStep4Image,
    handleConfirmDeleteImage,
    addGallerySlot,
    addGallerySlotsWithFiles,
    validateStep4Images,
    getFirstPendingStep4Upload,
    isStep4AllUploaded,
    coverInputRef,
    galleryInputRefs,
    pendingReplaceCoverId,
    pendingReplaceGalleryIds,
    savedImages,
  };
}
